import { t, Trans } from '@lingui/macro';
import { StoryText } from '@/components/microstory/text';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { Scatterplot } from '@/components/scatterplot';
import { olderGeneration, youngerGeneration } from '@/assets/messages';
import { countryIdToName } from '@/utils/country-id-to-name';
import { useState } from 'react';
import { AnnotatedType, PrimaryAnnotatedType } from '@/components/annotated-type';
import { useLingui } from '@lingui/react';

type FreeToBeYoungChapter2Props = {
  data: FreeToBeYoungData['people_suggested_higher_than_legal_female_marriage_age'];
};

export function FreeToBeYoungChapter2(props: FreeToBeYoungChapter2Props) {
  const { data } = props;

  const { i18n } = useLingui();

  const getChartLabel = (generation: string) =>
    t({
      id: 'free-to-be-young.chapter-2.chart-label-top',
      message: `More ${generation} propose a minimum marriage age for women higher than the current legal age`,
    });

  const chartData = (Object.entries(data) as [CountryIsoCode, AgeGroupBreakdown][]).map(
    ([key, value]) => ({
      id: key,
      label: countryIdToName(key),
      ...value,
    })
  );

  const [highlightedIds, setHighlightedIds] = useState<CountryIsoCode[]>();
  const [hasLabels, setHasLabels] = useState(true);

  const handleStepEnter = async (
    evt: OnStepEnterExitArgs<{ highlightedIds: CountryIsoCode[]; hasLabels: boolean }>
  ) => {
    setHighlightedIds(evt.data?.highlightedIds);
    setHasLabels(!!evt.data?.hasLabels);
  };

  return (
    <ScrollRoot>
      <ScrollGraphic>
        <Scatterplot
          data={chartData}
          topAxisLabel={getChartLabel(youngerGeneration(i18n))}
          bottomAxisLabel={getChartLabel(olderGeneration(i18n))}
          getXValue={(d) => d.older}
          getYValue={(d) => d.younger}
          highlightedIds={highlightedIds}
          hasLabels={hasLabels}
        />
      </ScrollGraphic>

      <ScrollSteps onStepEnter={handleStepEnter}>
        <ScrollStep>
          <StoryText>
            <Trans id="free-to-be-young.chapter-2.text-1">
              Young and older generations agree not only that children{' '}
              <AnnotatedType>deserve to have their voices heard.</AnnotatedType> They also agree
              that children deserve time to enjoy independence before marriage.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep
          data={{
            hasLabels: false,
            highlightedIds: [
              'AR',
              'BR',
              'ID',
              'LB',
              'PE',
              'BD',
              'CM',
              'ET',
              'IN',
              'KE',
              'ML',
              'MA',
              'NG',
              'UA',
              'ZW',
            ],
          }}
        >
          <StoryText>
            <Trans id="free-to-be-young.chapter-2.text-2">
              Most people in <PrimaryAnnotatedType>developing countries</PrimaryAnnotatedType> would
              prefer a minimum age for marriage above that specified by existing laws — and in many
              countries, older people are especially supportive.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep data={{ hasLabels: true, highlightedIds: ['CM', 'ID'] }}>
          <StoryText>
            <Trans id="free-to-be-young.chapter-2.text-3">
              The largest majorities proposing a higher legal marriage age for women are found in{' '}
              <PrimaryAnnotatedType>Indonesia</PrimaryAnnotatedType> and{' '}
              <PrimaryAnnotatedType>Cameroon</PrimaryAnnotatedType> — two countries where the legal
              marriage age for women is especially low.
            </Trans>
          </StoryText>
        </ScrollStep>
      </ScrollSteps>
    </ScrollRoot>
  );
}
